import { useUserSession } from '@solinkcorp/authentication';
import AnalyticsLogger from 'models/AnalyticsLogger';
import React from 'react';
import CloudAiApi, { type ImageUploadTarget } from 'src/clients/CloudAiApi';

/** S3 object tag applied on PUT; must match the Tagging signed into the presigned upload URL. */
export const VIDEO_SEARCH_IMAGE_S3_TAGGING = 'solink:service=videosearch';

export type ImageUploadStatus = 'idle' | 'uploading' | 'error';

export interface UseVideoSearchImageUpload {
  status: ImageUploadStatus;
  error: string | null;
  /**
   * Uploads the file to S3 via a presigned URL. Resolves with the upload target
   * (including the canonical s3 URI) on success, or `null` on failure.
   */
  upload: (file: File) => Promise<ImageUploadTarget | null>;
  reset: () => void;
}

/**
 * Encapsulates the video-search image upload flow: request a presigned upload target,
 * then PUT the raw file bytes directly to S3. Exposes a simple status the caller can use
 * to show an upload overlay.
 */
export default function useVideoSearchImageUpload(): UseVideoSearchImageUpload {
  const userSession = useUserSession();
  const token = userSession?.accessToken;

  const [status, setStatus] = React.useState<ImageUploadStatus>('idle');
  const [error, setError] = React.useState<string | null>(null);

  // Bumped whenever an upload starts or is reset, so a stale in-flight upload that
  // resolves after a newer upload (or a reset) can't clobber the shared status/error.
  const requestIdRef = React.useRef(0);

  const reset = React.useCallback(() => {
    requestIdRef.current += 1;
    setStatus('idle');
    setError(null);
  }, []);

  const upload = React.useCallback(
    async (file: File): Promise<ImageUploadTarget | null> => {
      if (!token) {
        setStatus('error');
        setError('You must be signed in to upload an image.');
        return null;
      }

      const requestId = (requestIdRef.current += 1);

      setStatus('uploading');
      setError(null);

      AnalyticsLogger.trackVideoSearchUpload({
        fileName: file.name,
        contentType: file.type,
        size: file.size,
      });

      try {
        const target = await CloudAiApi.requestImageUploadUrl(token, {
          fileName: file.name,
          contentType: file.type,
          size: file.size,
        });

        const putResponse = await fetch(target.uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type,
          },
          body: file,
        });

        if (!putResponse.ok) {
          throw new Error(`Upload failed: ${putResponse.status}`);
        }

        if (requestIdRef.current === requestId) {
          setStatus('idle');
        }
        return target;
      } catch (e) {
        console.error('Failed to upload video search image', e);
        if (requestIdRef.current === requestId) {
          setStatus('error');
          setError('Failed to upload image. Please try again.');
        }
        return null;
      }
    },
    [token],
  );

  return { status, error, upload, reset };
}
